import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';
import React, { useState } from 'react';

import { menuItemClassName } from '@/components/ui/dropdown-menu';

import {
    MenuItemProps,
    MenuRenderer,
    MenuSeparatorProps,
    SubmenuProps,
    ThreadMenuItemsProps,
    useThreadMenuItems
} from './ThreadMenuItems';

export default function ThreadContextMenu({
    onDelete,
    onRename,
    onAddToProject,
    onCreateNewProject,
    onRemoveFromProject,
    onPinToOrg,
    onShareThread,
    folders = [],
    isInProject = false
}: ThreadMenuItemsProps) {
    const [showAddToProjectSubmenu, setShowAddToProjectSubmenu] =
        useState(false);
    const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);

    const { renderMenuItems } = useThreadMenuItems({
        onDelete,
        onRename,
        onAddToProject,
        onCreateNewProject,
        onRemoveFromProject,
        onPinToOrg,
        onShareThread,
        folders,
        isInProject
    });

    // Context menu renderer implementation
    const contextMenuRenderer: MenuRenderer = {
        MenuItem: ({
            onClick,
            icon,
            children,
            className,
            destructive,
            style,
            onMouseEnter
        }: MenuItemProps) => (
            <div
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                className={cn(
                    menuItemClassName,
                    'hover:bg-hover',
                    destructive &&
                        'text-destructive hover:bg-destructive/10 [&_svg]:text-destructive',
                    className
                )}
                style={style}
            >
                {icon}
                {children}
            </div>
        ),
        MenuSeparator: ({ className }: MenuSeparatorProps) => (
            <div className={cn('bg-muted -mx-1 my-1 h-px', className)} />
        ),
        Submenu: ({ trigger, children }: SubmenuProps) => (
            <div
                className="relative"
                onMouseEnter={() => {
                    if (hideTimeout) {
                        clearTimeout(hideTimeout);
                        setHideTimeout(null);
                    }
                    setShowAddToProjectSubmenu(true);
                }}
                onMouseLeave={() => {
                    const timeout = setTimeout(
                        () => setShowAddToProjectSubmenu(false),
                        300
                    );
                    setHideTimeout(timeout);
                }}
            >
                <div className={cn(menuItemClassName, 'hover:bg-hover')}>
                    <div className="flex flex-1 items-center gap-2.5">
                        {trigger}
                    </div>
                    <ChevronRight className="ml-auto" />
                </div>

                {showAddToProjectSubmenu && (
                    <div
                        className="top-0 left-full z-50 absolute bg-popover shadow-lg p-1 border rounded-md min-w-48 overflow-visible text-popover-foreground"
                        data-context-menu
                        style={{ marginLeft: '4px' }}
                        onMouseEnter={() => {
                            if (hideTimeout) {
                                clearTimeout(hideTimeout);
                                setHideTimeout(null);
                            }
                            setShowAddToProjectSubmenu(true);
                        }}
                        onMouseLeave={() => {
                            const timeout = setTimeout(
                                () => setShowAddToProjectSubmenu(false),
                                300
                            );
                            setHideTimeout(timeout);
                        }}
                    >
                        {children}
                    </div>
                )}
            </div>
        )
    };

    const menuItems = renderMenuItems(contextMenuRenderer);

    return (
        <div
            className="z-50 bg-popover shadow-md p-1 border rounded-md min-w-32 overflow-visible text-popover-foreground"
            data-context-menu
        >
            {menuItems}
        </div>
    );
}
